import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useSheetStore } from "../../sheet.store";
import {
	QuickStartAttributeValue,
	useAttributesStore,
} from "@/dentlab/src/store/Attributes";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { RolesEnum } from "@/lib/types/roles.types";
import { supabase } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import {
	QUICKSTART_TASKS,
	QuickStartTaskId,
} from "@/dentlab/src/pages/quickstart/quickstart-tasks";
import { QuickstartSection } from "@/dentlab/src/pages/quickstart/components/QuickstartSection";
import { EinrichtungSection } from "@/dentlab/src/pages/quickstart/components/EinrichtungSection";
import { SheetContentProps } from "../../sheet-provider/sheetRegistry";

export const QuickstartSheetContent: React.FC<SheetContentProps> = ({
	sheetKey,
}) => {
	const navigate = useNavigate();
	const closeSheet = useSheetStore((state) => state.closeSheet);
	const role = useCentralStore((state) => state.role);
	const organizationId = useCentralStore((state) => state.organization?.id);
	const setQuickstartAttribute = useAttributesStore(
		(state) => state.setQuickstartAttribute
	);
	const getQuickstartTaskStatuses = useAttributesStore(
		(state) => state.getQuickstartTaskStatuses
	);
	useAttributesStore((state) => state.attributes);
	const isQuickstartEnabled = useFeatureFlagEnabled("quickstart");

	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!isQuickstartEnabled) {
			setLoading(false);
			return;
		}

		const reconcile = async () => {
			if (!organizationId) {
				setLoading(false);
				return;
			}

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const { data, error } = await (supabase.rpc as any)(
				"get_quickstart_completion_status",
				{ p_organization_id: organizationId }
			);

			if (error) {
				Logger.error(
					"Failed to get quickstart completion status: " +
						String(error.message)
				);
				setLoading(false);
				return;
			}

			if (data && typeof data === "object") {
				const statuses = getQuickstartTaskStatuses();
				const rpcResults = data as Record<string, boolean>;

				for (const [taskId, isComplete] of Object.entries(rpcResults)) {
					if (isComplete) {
						const currentStatus =
							statuses[taskId as QuickStartTaskId];
						if (
							currentStatus !== "skipped" &&
							currentStatus !== "completed"
						) {
							await setQuickstartAttribute(
								taskId as QuickStartTaskId,
								"completed"
							);
						}
					}
				}
			}

			setLoading(false);
		};

		reconcile();
	}, [organizationId, isQuickstartEnabled]);

	const handleCtaClick = (to: string) => {
		closeSheet(sheetKey);
		navigate(to);
	};

	const taskStatuses = getQuickstartTaskStatuses() as Record<
		QuickStartTaskId,
		QuickStartAttributeValue
	>;

	if (loading) {
		return (
			<div className="flex items-center justify-center p-8">
				<Loader2 className="h-6 w-6 animate-spin text-blue-600" />
			</div>
		);
	}

	return (
		<div className="p-4">
			{role === RolesEnum.OWNER && <EinrichtungSection />}
			{isQuickstartEnabled &&
				QUICKSTART_TASKS.map((section) => (
					<QuickstartSection
						key={section.id}
						section={section}
						taskStatuses={taskStatuses}
						onCtaClick={handleCtaClick}
					/>
				))}
		</div>
	);
};
